// src/pages/Notifications/Notification.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItemButton,
  Divider,
  Chip,
  useTheme,
  CircularProgress,
  Badge,
  Stack,
  Paper,
  ListItemText,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { useSelector, useDispatch } from 'react-redux';
import { fetchNotifications, markNotificationAsRead } from './../../store/Slices/NotificationSlice';

const notificationTypes = [
  'All',
  'Deposit',
  'Withdrawal',
  'Transfer',
  'SpotTrade',
  'FutureTrade',
  'AITrade',
  'Referral',
  'Support',
  'Other',
  'PredictionPayment',
];

const NotificationsPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useDispatch();

  const { notifications, loading, error, unreadCount } = useSelector(
    (state) => state.notifications
  );
  const user = useSelector((state) => state.user.data);

  const [filterType, setFilterType] = useState('All');
  const [filterRead, setFilterRead] = useState('All');
  const [filterTime, setFilterTime] = useState('All');

  useEffect(() => {
    if (user && user._id) {
      dispatch(fetchNotifications(user._id));
    } else {
      console.log('EROOOOOOOOOOORRRRRRRRRRRRRR');
    }
  }, [user, dispatch]);

  const handleMarkAsRead = (notificationId, isRead) => {
    if (!isRead) {
      dispatch(markNotificationAsRead(notificationId));
    }
  };

  const filteredNotifications = useMemo(() => {
    return notifications.filter((notification) => {
      const typeMatch =
        filterType === 'All' || notification.type === filterType;

      const readMatch =
        filterRead === 'All' ||
        (filterRead === 'Read'
          ? notification.is_read
          : !notification.is_read);

      const createdAt = new Date(notification.created_at);
      const now = new Date();
      const diffDays =
        (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24);

      const timeMatch =
        filterTime === 'All' ||
        (filterTime === 'Today' &&
          createdAt.toDateString() === now.toDateString()) ||
        (filterTime === 'Last 7 Days' && diffDays <= 7);

      return typeMatch && readMatch && timeMatch;
    });
  }, [notifications, filterType, filterRead, filterTime]);

  const hasNotifications = !loading && !error && filteredNotifications.length > 0;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: theme.palette.background.default,
        pb: 4,
      }}
    >
      {/* Top AppBar */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: theme.palette.background.navbar,
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${
            theme.palette.mode === 'dark'
              ? 'rgba(255,255,255,0.06)'
              : 'rgba(0,0,0,0.06)'
          }`,
        }}
      >
        <Toolbar>
          {/* Back button */}
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate(-1)}
            sx={{ mr: 1 }}
          >
            <ArrowBackIcon />
          </IconButton>

          {/* Title centered */}
          <Typography
            variant="h6"
            component="div"
            sx={{
              flexGrow: 1,
              textAlign: 'center',
              fontWeight: 600,
              letterSpacing: 0.3,
            }}
          >
            Notifications
          </Typography>

          {/* Notification icon + badge on the right */}
          <IconButton edge="end" color="inherit" sx={{ ml: 1 }}>
            <Badge
              badgeContent={unreadCount || 0}
              color="primary"
              invisible={!unreadCount}
              sx={{
                '& .MuiBadge-badge': {
                  bgcolor: theme.palette.brand.main,
                  color: '#000',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  minWidth: 18,
                  height: 18,
                },
              }}
            >
              <NotificationsNoneIcon />
            </Badge>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box
        sx={{
          mt: 3, // تم تقليل المسافة بعد AppBar
          px: { xs: 2, md: 4 },
          py: 2,
          maxWidth: '1100px',
          mx: 'auto',
        }}
      >
        {/* Header & Filters Container */}
        <Paper
          sx={{
            mb: 3,
            p: { xs: 2, md: 3 },
            borderRadius: 3,
            bgcolor: theme.palette.background.paper,
            border: `1px solid ${
              theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.06)'
                : 'rgba(0,0,0,0.06)'
            }`,
            boxShadow:
              theme.palette.mode === 'dark'
                ? '0 18px 45px rgba(0,0,0,0.7)'
                : '0 14px 35px rgba(15,23,42,0.12)',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, mb: 0.5, letterSpacing: 0.2 }}
              >
                Notification Center
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary }}
              >
                Track your deposits, trades, and system alerts in one place.
              </Typography>
            </Box>

            <Chip
              label={
                unreadCount > 0
                  ? `${unreadCount} unread`
                  : 'All notifications read'
              }
              size="small"
              sx={{
                bgcolor:
                  unreadCount > 0
                    ? 'rgba(243,186,47,0.14)'
                    : 'rgba(148,163,184,0.12)',
                color:
                  unreadCount > 0
                    ? theme.palette.brand.main
                    : theme.palette.text.secondary,
                fontWeight: 500,
                borderRadius: 2,
              }}
            />
          </Stack>

          {/* Filters */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            sx={{ mt: 1 }}
          >
            <FormControl
              size="small"
              sx={{ minWidth: { xs: '100%', md: 180 } }}
            >
              <InputLabel>Type</InputLabel>
              <Select
                value={filterType}
                label="Type"
                onChange={(e) => setFilterType(e.target.value)}
              >
                {notificationTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type === 'SpotTrade'
                      ? 'Spot Trade'
                      : type === 'FutureTrade'
                      ? 'Futures Trade'
                      : type === 'AITrade'
                      ? 'AI Trade'
                      : type === 'PredictionPayment'
                      ? 'Prediction Payment'
                      : type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              size="small"
              sx={{ minWidth: { xs: '100%', md: 160 } }}
            >
              <InputLabel>Status</InputLabel>
              <Select
                value={filterRead}
                label="Status"
                onChange={(e) => setFilterRead(e.target.value)}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Read">Read</MenuItem>
                <MenuItem value="Unread">Unread</MenuItem>
              </Select>
            </FormControl>

            <FormControl
              size="small"
              sx={{ minWidth: { xs: '100%', md: 160 } }}
            >
              <InputLabel>Time</InputLabel>
              <Select
                value={filterTime}
                label="Time"
                onChange={(e) => setFilterTime(e.target.value)}
              >
                <MenuItem value="All">All Time</MenuItem>
                <MenuItem value="Today">Today</MenuItem>
                <MenuItem value="Last 7 Days">Last 7 Days</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Paper>

        {/* Notifications List / States */}
        {loading && (
          <Box
            sx={{
              py: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <CircularProgress />
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary }}
            >
              Loading your notifications...
            </Typography>
          </Box>
        )}

        {error && !loading && (
          <Box
            sx={{
              py: 6,
              textAlign: 'center',
            }}
          >
            <Typography color="error" sx={{ mb: 1 }}>
              {error}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary }}
            >
              Please try again in a few moments.
            </Typography>
          </Box>
        )}

        {!loading && !error && !hasNotifications && (
          <Box
            sx={{
              py: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1,
                bgcolor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(148,163,184,0.16)'
                    : 'rgba(148,163,184,0.12)',
              }}
            >
              <NotificationsNoneIcon
                sx={{
                  fontSize: 32,
                  color: theme.palette.text.secondary,
                }}
              />
            </Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, mb: 0.5 }}
            >
              No notifications found
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                maxWidth: 360,
                textAlign: 'center',
              }}
            >
              Once you start depositing, trading, or using AI and Futures, your
              alerts will appear here in real time.
            </Typography>
          </Box>
        )}

        {hasNotifications && (
          <Paper
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              bgcolor: theme.palette.background.paper,
              border: `1px solid ${
                theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.04)'
                  : 'rgba(148,163,184,0.25)'
              }`,
              boxShadow:
                theme.palette.mode === 'dark'
                  ? '0 18px 45px rgba(0,0,0,0.8)'
                  : '0 14px 40px rgba(15,23,42,0.14)',
            }}
          >
            <List disablePadding>
              {filteredNotifications.map((notification, index) => {
                const isUnread = !notification.is_read;

                return (
                  <React.Fragment key={notification._id}>
                    <ListItemButton
                      onClick={() =>
                        handleMarkAsRead(notification._id, notification.is_read)
                      }
                      sx={{
                        alignItems: 'flex-start',
                        py: 2.1,
                        px: { xs: 2.2, md: 3 },
                        bgcolor: isUnread
                          ? theme.palette.mode === 'dark'
                            ? 'rgba(243,186,47,0.08)'
                            : 'rgba(243,186,47,0.06)'
                          : 'transparent',
                        borderLeft: isUnread
                          ? `3px solid ${theme.palette.brand.main}`
                          : '3px solid transparent',
                        transition:
                          'background-color 0.18s ease, transform 0.12s ease',
                        '&:hover': {
                          bgcolor:
                            theme.palette.mode === 'dark'
                              ? 'rgba(148,163,184,0.16)'
                              : 'rgba(15,23,42,0.03)',
                          transform: 'translateY(-1px)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          flex: 1,
                          gap: 0.6,
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={1.5}
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ minWidth: 0 }}
                          >
                            {isUnread && (
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: theme.palette.brand.main,
                                  flexShrink: 0,
                                }}
                              />
                            )}

                            <Typography
                              variant="subtitle1"
                              noWrap
                              sx={{ fontWeight: isUnread ? 600 : 500 }}
                            >
                              {notification.title}
                            </Typography>
                          </Stack>

                          <Chip
                            label={
                              notification.type === 'SpotTrade'
                                ? 'Spot'
                                : notification.type === 'FutureTrade'
                                ? 'Futures'
                                : notification.type === 'AITrade'
                                ? 'AI'
                                : notification.type === 'PredictionPayment'
                                ? 'Prediction'
                                : notification.type
                            }
                            size="small"
                            sx={{
                              ml: 1,
                              bgcolor:
                                notification.type === 'Deposit'
                                  ? 'rgba(22,163,74,0.18)'
                                  : notification.type === 'Withdrawal'
                                  ? 'rgba(220,38,38,0.16)'
                                  : notification.type === 'Support'
                                  ? 'rgba(59,130,246,0.16)'
                                  : 'rgba(148,163,184,0.14)',
                              color:
                                notification.type === 'Deposit'
                                  ? '#22c55e'
                                  : notification.type === 'Withdrawal'
                                  ? '#f97373'
                                  : notification.type === 'Support'
                                  ? '#60a5fa'
                                  : theme.palette.text.secondary,
                              borderRadius: 2,
                              fontWeight: 500,
                            }}
                          />
                        </Stack>

                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.secondary,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {notification.message}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.disabled,
                            mt: 0.2,
                          }}
                        >
                          {new Date(
                            notification.created_at
                          ).toLocaleString()}
                        </Typography>
                      </Box>
                    </ListItemButton>

                    {index < filteredNotifications.length - 1 && (
                      <Divider
                        component="li"
                        sx={{
                          borderColor:
                            theme.palette.mode === 'dark'
                              ? 'rgba(30,41,59,0.9)'
                              : 'rgba(226,232,240,0.9)',
                        }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </List>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default NotificationsPage;
